/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { Observable, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { ofType, StateObservable } from 'redux-observable';
import { AjaxError, AjaxResponse } from 'rxjs/ajax';
import { Action, ActionCreators } from './actions';
import { RootState } from '../index';
import { handleErrorInterceptor } from '../../utils/AuthUtils';

// Getting list of contracts
const requestContractsSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedContractsSuccess.create(response);
};

const requestContractsError = (error: AjaxError): any => {
  return ActionCreators.ReceivedContractsError.create(error);
};

export const requestContractsEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestContracts.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/contracts`).pipe(
          map((response: AjaxResponse<any>): Action => requestContractsSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(requestContractsError(error))),
          ),
        ),
    ),
  );

// Request Current Contract
const receivedCurrentContractSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ReceivedCurrentContractSuccess.create(response);
};

const receivedCurrentContractError = (error: AjaxError): any => {
  return ActionCreators.ReceivedCurrentContractError.create(error);
};

export const requestCurrentContractEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.RequestCurrentContract.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.get(`/provisioning/contracts/${$action.payload}`).pipe(
          map((response: AjaxResponse<any>): Action => receivedCurrentContractSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(receivedCurrentContractError(error))),
          ),
        ),
    ),
  );

// Adding a contract
const contractProvisioningSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.ContractProvisioningSuccess.create(response);
};

const contractProvisioningError = (error: AjaxError): any => {
  return ActionCreators.ContractProvisioningError.create(error);
};

export const provisionContractEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.ProvisionContract.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.post('/provisioning/contracts', $action.payload).pipe(
          map((response: AjaxResponse<any>): Action => contractProvisioningSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> =>
              handleErrorInterceptor(error, of(contractProvisioningError(error))),
          ),
        ),
    ),
  );

// Delete a contract
const deleteContractSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.DeleteContractSuccess.create(response);
};

const deleteContractError = (error: AjaxError): any => {
  return ActionCreators.DeleteContractError.create(error);
};

export const deleteContractEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.DeleteContract.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.delete(`/provisioning/contracts/${$action.payload}`).pipe(
          switchMap(
            (response: AjaxResponse<any>): Array<Action> => [
              deleteContractSuccess(response),
              ActionCreators.RequestContracts.create(),
            ],
          ),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(deleteContractError(error))),
          ),
        ),
    ),
  );

// Update a contract
const updateContractSuccess = (response: AjaxResponse<any>): any => {
  return ActionCreators.UpdateContractSuccess.create(response);
};

const updateContractError = (error: AjaxError): any => {
  return ActionCreators.UpdateContractError.create(error);
};

export const updateContractEpic = (
  action$: Observable<Action>,
  state$: StateObservable<RootState>,
  { ajaxRaw }: any,
): Observable<Action> =>
  action$.pipe(
    ofType(ActionCreators.UpdateContract.type),
    switchMap(
      ($action: any): Observable<Action> =>
        ajaxRaw.put(`/provisioning/contracts/${$action.payload.id}`).pipe(
          map((response: AjaxResponse<any>): Action => updateContractSuccess(response)),
          catchError(
            (error: AjaxError): Observable<Action> => handleErrorInterceptor(error, of(updateContractError(error))),
          ),
        ),
    ),
  );
